<template>
  <div>
    <h4 class="text-h4">
      {{ $t("pages.locationTypes.form.edit.title") }} {{ form.name }}
    </h4>
    <v-spacer class="mb-6"></v-spacer>
    <div>
      <v-row no-gutters>
        <v-col col="12" lg="8">
          <form @submit.prevent="submit">
            <v-card elevation="1">
              <v-card-text>
                <v-row no-gutters>
                  <v-col cols="4">
                    <v-subheader>{{
                      $t("common.fieldNames.name")
                    }}</v-subheader>
                  </v-col>
                  <v-col cols="8">
                    <v-text-field
                      v-model="form.name"
                      name="name"
                      type="text"
                      :disabled="isContentDragging"
                      :error-messages="form.errors.name"
                    />
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="4">
                    <v-subheader>{{
                      $t("common.fieldNames.lastUpdated")
                    }}</v-subheader>
                  </v-col>
                  <v-col cols="8">
                    <v-text-field
                      v-model="updatedAt"
                      name="updatedAt"
                      disabled
                      type="text"
                    />
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="4">
                    <v-subheader>{{
                      $t("common.fieldNames.createdAt")
                    }}</v-subheader>
                  </v-col>
                  <v-col cols="8">
                    <v-text-field
                      v-model="createdAt"
                      name="createdAt"
                      disabled
                      type="text"
                    />
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions>
                <request-size-error :errors="form.errors" />
                <flash-messages />
              </v-card-actions>
            </v-card>
            <vue-draggable
              v-model="form.contents"
              group="contents"
              @choose="onDraggingStart"
              @unchoose="onDraggingEnd"
              @end="onDraggingEnd"
            >
              <v-card
                elevation="1"
                v-for="(content, index) in form.contents"
                :key="index"
                class="mt-4 cursor-grab"
              >
                <v-card-title class="pb-1">
                  {{ content.informationType.name }}
                  <v-spacer />
                  <v-btn icon @click="removeContent(index)"
                    ><v-icon>mdi-trash-can</v-icon></v-btn
                  >
                </v-card-title>
                <v-card-text>
                  <v-row no-gutters class="align-center">
                    <v-col cols="4">
                      <v-subheader>{{
                        $t("components.contents.visibility")
                      }}</v-subheader>
                    </v-col>
                    <v-col cols="8">
                      <v-radio-group
                        v-model="content.visibility"
                        row
                        class="mt-1 visibility-buttons"
                        :error-messages="
                          form.errors[`contents${index}Visibility`]
                        "
                      >
                        <v-radio
                          v-for="visibility in visibilities"
                          :key="visibility"
                          :label="$t(`common.visibilities.${visibility}`)"
                          :value="visibility"
                        />
                      </v-radio-group>
                    </v-col>
                  </v-row>
                  <editor
                    v-model="content.description"
                    :disabled="isContentDragging"
                  />
                </v-card-text>
                <v-card-text>
                  <v-file-input
                    v-model="content.newFiles"
                    :placeholder="
                      $t('components.contents.addFiles.placeholder')
                    "
                    :label="$t('components.contents.addFiles.label')"
                    multiple
                    prepend-icon="mdi-paperclip"
                  >
                    <template v-slot:selection="{ text }">
                      <v-chip small color="primary">
                        {{ text }}
                      </v-chip>
                    </template>
                  </v-file-input>
                </v-card-text>
                <v-card-text v-if="content.files && content.files.length">
                  <v-simple-table fixed-header>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">
                            {{ $t("common.fieldNames.name") }}
                          </th>
                          <th class="text-left">
                            {{ $t("common.fieldNames.createdAt") }}
                          </th>
                          <th class="text-right"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(item, fIndex) in content.files"
                          :key="item.id"
                        >
                          <td>{{ item.name }}{{ item.extension }}</td>
                          <td>
                            {{ new Date(item.createdAt).toLocaleString() }}
                          </td>
                          <td class="text-right">
                            <v-btn
                              small
                              text
                              @click.stop
                              :href="
                                route('api.files.content', {
                                  id: item.id,
                                  filename: item.originalName,
                                })
                              "
                              target="_blank"
                            >
                              <v-icon class="mr-2">mdi-attachment</v-icon>
                            </v-btn>
                            <v-btn
                              small
                              text
                              @click.stop="removeFile(index, fIndex)"
                            >
                              <v-icon class="mr-2"
                                >mdi-trash-can-outline</v-icon
                              >
                            </v-btn>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card-text>
              </v-card>
            </vue-draggable>
          </form>
        </v-col>
      </v-row>
    </div>
    <content-dialog
      v-model="dialog"
      :contents="informationTypes"
      @submit="addContent"
    />

    <v-speed-dial v-model="fab" fixed fab bottom right>
      <template v-slot:activator>
        <v-btn
          fab
          color="primary"
          v-if="fab"
          :disabled="sending || form.processing"
          @click="submit"
        >
          <v-icon>mdi-content-save</v-icon>
        </v-btn>
        <v-btn fab color="secondary" v-else>
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </template>
      <v-tooltip
        :nudge-left="20"
        left
        :value="true"
        class="v-tooltip--speed-dial"
      >
        <template #activator="{ on }">
          <v-btn fab dark small color="indigo" v-on="on" @click="openDialog">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("pages.locationTypes.form.menu.addContent") }}</span>
      </v-tooltip>
      <v-tooltip
        :nudge-left="20"
        left
        :value="true"
        class="v-tooltip--speed-dial"
      >
        <template #activator="{ on }">
          <v-btn
            fab
            dark
            small
            color="red"
            v-on="on"
            :disabled="sending || form.processing"
            @click="destroy"
          >
            <v-icon>mdi-trash-can</v-icon>
          </v-btn>
        </template>
        <span>{{
          $t("pages.locationTypes.form.menu.deleteLocationType")
        }}</span>
      </v-tooltip>
    </v-speed-dial>
  </div>
</template>

<script>
import { appLayout } from "@/util/layout";
import { objectToFormData } from "@/util/formData";
import { pascalToSpaces } from "@/util/text";
import Editor from "@/Shared/Editor";
import FlashMessages from "@/Shared/FlashMessages";
import ContentDialog from "@/Shared/ContentDialog";
import RequestSizeError from "@/Shared/RequestSizeError";

export default {
  layout: appLayout({ title: "Location type Edit" }),
  props: {
    locationType: Object,
    informationTypes: Array,
    visibilities: Array,
  },
  components: {
    FlashMessages,
    Editor,
    ContentDialog,
    RequestSizeError,
  },
  data() {
    return {
      dialog: false,
      fab: false,
      sending: false,
      form: this.$inertia.form({
        id: this.locationType.id,
        name: this.locationType.name,
        contents: this.locationType.contents,
      }),
      selectedContentTypes: [],
      isContentDragging: false,
    };
  },
  computed: {
    createdAt() {
      return new Date(this.locationType.createdAt).toLocaleString();
    },
    updatedAt() {
      return new Date(this.locationType.createdAt).toLocaleString();
    },
  },
  methods: {
    addContent(contents) {
      this.form.contents = this.form.contents.concat(contents);
    },
    removeContent(index) {
      this.form.contents.splice(index, 1);
    },
    removeFile(contentIndex, fileIndex) {
      this.form.contents[contentIndex].files.splice(fileIndex, 1);
    },
    openDialog() {
      this.dialog = true;
    },
    destroy() {
      if (
        window.confirm("Are you sure you want to delete this location type?")
      ) {
        this.$inertia.delete(
          this.route("location-types.destroy", this.locationType.id),
          {
            onStart: () => (this.sending = true),
            onFinish: () => (this.sending = false),
          }
        );
      }
    },
    submit() {
      this.form
        .transform((data) => {
          const transformedData = {
            ...data,
            contents: data.contents.map((c, index) => ({
              ...c,
              sortOrder: index + 1,
            })),
          };

          return objectToFormData(transformedData);
        })
        .put(this.route("location-types.update", this.locationType.id), {
          preserveScroll: true,
          preserveState: false,
        });
    },
    pascalToSpaces(text) {
      return pascalToSpaces(text);
    },
    onDraggingStart() {
      this.isContentDragging = true;
    },
    onDraggingEnd() {
      this.isContentDragging = false;
    },
  },
};
</script>
